import { useState, useCallback } from 'react';
import { usePlatformAdminAuthStore } from '../../stores/platform-admin-auth.store';
import {
  featureFlagsService,
  FeatureFlag,
  OrganizationFeatureFlag,
  CreateFeatureFlagDto,
  UpdateFeatureFlagDto,
  EnableFeatureFlagDto,
  DisableFeatureFlagDto,
} from '../../services/platform-admin/feature-flags.service';

export function useFeatureFlags() {
  const token = usePlatformAdminAuthStore((state) => state.token);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAllFlags = useCallback(async (): Promise<FeatureFlag[] | null> => {
    if (!token) {
      setError('Not authenticated');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const flags = await featureFlagsService.getAllFlags(token);
      return flags;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch feature flags');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const getFlagById = useCallback(
    async (id: string): Promise<FeatureFlag | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const flag = await featureFlagsService.getFlagById(token, id);
        return flag;
      } catch (err: any) {
        setError(err.message || 'Failed to fetch feature flag');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const createFlag = useCallback(
    async (data: CreateFeatureFlagDto): Promise<FeatureFlag | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const flag = await featureFlagsService.createFlag(token, data);
        return flag;
      } catch (err: any) {
        setError(err.message || 'Failed to create feature flag');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const updateFlag = useCallback(
    async (id: string, data: UpdateFeatureFlagDto): Promise<FeatureFlag | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const flag = await featureFlagsService.updateFlag(token, id, data);
        return flag;
      } catch (err: any) {
        setError(err.message || 'Failed to update feature flag');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const deleteFlag = useCallback(
    async (id: string): Promise<boolean> => {
      if (!token) {
        setError('Not authenticated');
        return false;
      }

      setIsLoading(true);
      setError(null);

      try {
        await featureFlagsService.deleteFlag(token, id);
        return true;
      } catch (err: any) {
        setError(err.message || 'Failed to delete feature flag');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const enableForOrganizations = useCallback(
    async (flagId: string, data: EnableFeatureFlagDto): Promise<boolean> => {
      if (!token) {
        setError('Not authenticated');
        return false;
      }

      setIsLoading(true);
      setError(null);

      try {
        await featureFlagsService.enableForOrganizations(token, flagId, data);
        return true;
      } catch (err: any) {
        setError(err.message || 'Failed to enable feature flag');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const disableForOrganizations = useCallback(
    async (flagId: string, data: DisableFeatureFlagDto): Promise<boolean> => {
      if (!token) {
        setError('Not authenticated');
        return false;
      }

      setIsLoading(true);
      setError(null);

      try {
        await featureFlagsService.disableForOrganizations(token, flagId, data);
        return true;
      } catch (err: any) {
        setError(err.message || 'Failed to disable feature flag');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const getOrganizationsWithFlag = useCallback(
    async (flagId: string): Promise<OrganizationFeatureFlag[] | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const orgs = await featureFlagsService.getOrganizationsWithFlag(token, flagId);
        return orgs;
      } catch (err: any) {
        setError(err.message || 'Failed to fetch organizations');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const getFlagsForOrganization = useCallback(
    async (organizationId: string): Promise<FeatureFlag[] | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const flags = await featureFlagsService.getFlagsForOrganization(
          token,
          organizationId
        );
        return flags;
      } catch (err: any) {
        setError(err.message || 'Failed to fetch organization flags');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const isFeatureEnabled = useCallback(
    async (organizationId: string, flagKey: string): Promise<boolean> => {
      if (!token) {
        return false;
      }

      try {
        return await featureFlagsService.isFeatureEnabled(token, organizationId, flagKey);
      } catch (err: any) {
        return false;
      }
    },
    [token]
  );

  return {
    isLoading,
    error,
    getAllFlags,
    getFlagById,
    createFlag,
    updateFlag,
    deleteFlag,
    enableForOrganizations,
    disableForOrganizations,
    getOrganizationsWithFlag,
    getFlagsForOrganization,
    isFeatureEnabled,
  };
}
